import React from 'react';
import { ProjectAnalysis } from './project-analysis.type';
import { Division } from '../glossary/glossary.type';
import { deepCopy } from 'common/utils/common.util';
import { handleError } from 'common/utils/util.errors';
import ProjectAnalysisRenderer from './ProjectAnalysisRenderer';
import { toast } from 'react-toastify';
import { getProjectAnalysis, getProjectDraws } from '../project/project.api';
import { FileUploadErrorDTO, ProjectDraw, SaveProjectAnalysisDTO } from '../project/project.type';
import { saveProjectAnalysisData } from '../project/project-draws/project-draws.api';
import { useQueryClient } from 'react-query';
import keys from 'common/react-query/data-keys';
import { getProcessedLineItems } from '../project/project.util';

interface Props {
  initialProjectAnalysis: ProjectAnalysis;
  divisions: Division[];
  projectErrors: FileUploadErrorDTO[];
}
export const ProjectAnalysisWrapper: React.FC<Props> = ({ initialProjectAnalysis, divisions, projectErrors }) => {
  const [projectAnalysis, setProjectAnalysis] = React.useState<ProjectAnalysis>(deepCopy(initialProjectAnalysis));
  const [isProjectAnalysisDirty, setIsProjectAnalysisDirty] = React.useState(false);
  const [isProjectAnalysisLoading, setIsProjectAnalysisLoading] = React.useState(false);
  const [projectAnalysisDraws, setProjectAnalysisDraws] = React.useState<ProjectDraw[]>([]);
  const queryClient = useQueryClient();

  React.useEffect(() => {
    setProjectAnalysis(deepCopy(initialProjectAnalysis));
    setIsProjectAnalysisDirty(false);
  }, [initialProjectAnalysis]);

  React.useEffect(() => {
    fetchProjectDraws(projectAnalysis.projectId).then((projectDraws: ProjectDraw[]) => {
      setProjectAnalysisDraws(projectDraws);
    });
  }, [projectAnalysis.projectId]);

  const updateProjectAnalysisLocally = (updatedProjectAnalysis: ProjectAnalysis) => {
    setProjectAnalysis(updatedProjectAnalysis);
    setIsProjectAnalysisDirty(true);
  };

  const resetProjectAnalysis = () => {
    setProjectAnalysis(deepCopy(initialProjectAnalysis));
    setIsProjectAnalysisDirty(false);
  };

  const saveProjectAnalysis = async () => {
    try {
      setIsProjectAnalysisLoading(true);
      const { createdLineItems, editedLineItems, deletedLineItemIds } = getProcessedLineItems(projectAnalysis.lineItems);

      const requestData: SaveProjectAnalysisDTO = {
        projectId: projectAnalysis.projectId,
        metadata: {
          name: projectAnalysis.projectTitle,
          grossBuildingSF: projectAnalysis.grossBuildingSF,
          netRentableSF: projectAnalysis.netRentableSF,
          unitCount: projectAnalysis.unitCount,
          buildingCount: projectAnalysis.buildingCount,
          storyCount: projectAnalysis.storyCount,
          siteAcreage: projectAnalysis.siteAcreage,
        },
        createdLineItems,
        editedLineItems,
        deletedLineItemIds,
      };

      await saveProjectAnalysisData(projectAnalysis.projectId, requestData);

      toast.success('All Changes Saved');
      setIsProjectAnalysisDirty(false);
      queryClient.invalidateQueries([keys.project, projectAnalysis.projectId]);
      const updatedProjectAnalysis = await getProjectAnalysis(projectAnalysis.projectId);
      setProjectAnalysis(updatedProjectAnalysis);
    } catch (error) {
      handleError(error);
    } finally {
      setIsProjectAnalysisLoading(false);
    }
  };

  const refetchProjectAnalysis = async () => {
    try {
      setIsProjectAnalysisLoading(true);
      const updatedProjectAnalysis = await getProjectAnalysis(projectAnalysis.projectId);
      setProjectAnalysis(updatedProjectAnalysis);
    } catch (error) {
      handleError(error);
    } finally {
      setIsProjectAnalysisLoading(false);
    }
  };

  return (
    <ProjectAnalysisRenderer
      projectAnalysis={projectAnalysis}
      updateProjectAnalysis={updateProjectAnalysisLocally}
      resetProjectAnalysis={resetProjectAnalysis}
      saveProjectAnalysis={saveProjectAnalysis}
      refetchProjectAnalysis={refetchProjectAnalysis}
      isProjectAnalysisDirty={isProjectAnalysisDirty}
      isProjectAnalysisLoading={isProjectAnalysisLoading}
      divisions={divisions}
      projectAnalysisDraws={projectAnalysisDraws}
      projectErrors={projectErrors}
    />
  );
};

const fetchProjectDraws = async (projectId: string) => {
  try {
    const draws = await getProjectDraws(projectId);
    return draws;
  } catch (error) {
    handleError(error);
    return [];
  }
};
